import { log } from '@methanesat/log';

/**
 * Returns cookie value by key.
 * @warning Treat this as UNSAFE UGC because anyone can arbitrarily change their cookies.
 *
 * Note 1: cookies add extra size overhead because they're sent with EVERY request, so they should
 * be used sparingly (i.e. only for things the server needs to know about), otherwise it's
 * recommended to use local and session storage instead.
 *
 * Note 2: we don't control all cookies used by Portal, some like CookieConsent and edf_cc are set
 * by EDF scripts for analytics.
 */
export const getUNSAFECookieByKey = (key: string, rawAllCookies = document.cookie): string | null => {
    // Adapted from https://developer.mozilla.org/en-US/docs/Web/API/Document/cookie#example_2_get_a_sample_cookie_named_test2
    const allCookies = rawAllCookies.split('; ');
    const foundEntry = allCookies.find((curKey) => curKey.startsWith(`${key}=`));
    return foundEntry ? foundEntry.split('=')[1] : null;
};

/**
 * Returns cookie value as JSON by key.
 * @warning Treat this as UNSAFE UGC because anyone can arbitrarily change their cookies.
 */
export const getUNSAFECookieJSONByKey = <T>(key: string, allCookies = document.cookie): T | null => {
    const UNSAFEValue = getUNSAFECookieByKey(key, allCookies);

    let returnValue: T | null = null;
    if (UNSAFEValue === null) {
        return null;
    } else {
        try {
            returnValue = JSON.parse(UNSAFEValue);
        } catch (err) {
            log.error(`Error parsing JSON cookie '${key}'`);
        }
    }

    return returnValue;
};

/**
 * Determines how much personalization the user has allowed us.
 * true = the user explicitly enabled nonessential cookies
 * false = the user explicitly denied nonessential cookies
 * null = unknown (cookie not set)
 */
export const doesUserAllowNonessentialCookies = (allCookies = document.cookie) => {
    const edfCCCookie = getUNSAFECookieJSONByKey<{ categories?: string[]; level?: string[] }>('edf_cc', allCookies);

    if (edfCCCookie === null) {
        return null;
    }

    const level = edfCCCookie.level ?? [];
    return level.includes('personalization');
};
